import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Dish, OrderItem, CartOrder, Extra, Pack } from '../types';
import { useAuth } from './AuthContext';

interface OrderContextType {
  cartOrders: CartOrder[];
  addToOrder: (item: Dish | Pack, restaurantName: string, restaurantId: string, selectedExtras?: Extra[]) => void;
  updateQuantity: (itemId: string | number, newQuantity: number, restaurantId: string) => void;
  updateItemExtras: (itemId: string | number, restaurantId: string, extras: Extra[]) => void;
  totalPrice: number;
  totalItems: number;
  clearOrder: (restaurantId?: string) => void;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

const EXPIRY_TIME = 15 * 60 * 1000; // 15 minutes in ms

export const OrderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const userId = currentUser ? currentUser.uid : 'guest';
  const cartKey = `yumii_multi_cart_${userId}`;

  // Initialize from localStorage based on current user
  const [cartOrders, setCartOrders] = useState<CartOrder[]>(() => {
    try {
      const saved = localStorage.getItem(cartKey);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to parse cart from localStorage", e);
      return [];
    }
  });

  // Switch cart when user changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(cartKey);
      if (saved) {
        setCartOrders(JSON.parse(saved));
      } else {
        setCartOrders([]);
      }
    } catch (e) {
      console.error("Failed to load cart for user", userId, e);
      setCartOrders([]);
    }
  }, [userId, cartKey]);

  // Persist to localStorage whenever cartOrders changes
  useEffect(() => {
    try {
      localStorage.setItem(cartKey, JSON.stringify(cartOrders));
    } catch (e) {
      console.error("Failed to save cart to localStorage", e);
    }
  }, [cartOrders, cartKey]);

  // Auto-expiry logic
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCartOrders(prev => {
        const filtered = prev.filter(order => now - order.lastUpdated < EXPIRY_TIME);
        if (filtered.length !== prev.length) {
          console.log("[OrderContext] Automatically removed expired unconfirmed orders.");
          return filtered;
        }
        return prev;
      });
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const addToOrder = (incomingItem: Dish | Pack, restaurantName: string, restaurantId: string, selectedExtras: Extra[] = []) => {
    const isPack = 'packPrice' in incomingItem;
    
    setCartOrders(prevOrders => {
      const existingOrderIndex = prevOrders.findIndex(o => o.restaurantId === restaurantId);
      const now = Date.now();

      if (existingOrderIndex > -1) {
        // Update existing order for this restaurant
        const updatedOrders = [...prevOrders];
        const items = [...updatedOrders[existingOrderIndex].items];
        
        // Find existing item by ID AND type (to avoid collisions between dish ID and pack ID if they ever overlap)
        const existingItemIndex = items.findIndex(item => {
            const currentId = item.type === 'pack' ? item.pack?.id : item.dish?.id;
            return currentId === incomingItem.id && item.type === (isPack ? 'pack' : 'dish');
        });

        if (existingItemIndex > -1) {
          items[existingItemIndex] = {
            ...items[existingItemIndex],
            quantity: items[existingItemIndex].quantity + 1
          };
        } else {
          items.push({ 
            dish: !isPack ? (incomingItem as Dish) : undefined,
            pack: isPack ? (incomingItem as Pack) : undefined,
            type: isPack ? 'pack' : 'dish',
            quantity: 1, 
            restaurantName, 
            restaurantId, 
            selectedExtras 
          });
        }

        updatedOrders[existingOrderIndex] = {
          ...updatedOrders[existingOrderIndex],
          items,
          lastUpdated: now
        };
        return updatedOrders;
      } else {
        // Create new order block for this restaurant
        return [
          ...prevOrders,
          {
            restaurantId,
            restaurantName,
            items: [{ 
                dish: !isPack ? (incomingItem as Dish) : undefined,
                pack: isPack ? (incomingItem as Pack) : undefined,
                type: isPack ? 'pack' : 'dish',
                quantity: 1, 
                restaurantName, 
                restaurantId, 
                selectedExtras 
            }],
            lastUpdated: now
          }
        ];
      }
    });
  };

  const updateQuantity = (itemId: string | number, newQuantity: number, restaurantId: string) => {
    setCartOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.restaurantId === restaurantId) {
          const updatedItems = newQuantity <= 0
            ? order.items.filter(item => {
                const currentId = item.type === 'pack' ? item.pack?.id : item.dish?.id;
                return currentId !== itemId;
            })
            : order.items.map(item => {
                const currentId = item.type === 'pack' ? item.pack?.id : item.dish?.id;
                return currentId === itemId ? { ...item, quantity: newQuantity } : item;
            });

          return { ...order, items: updatedItems, lastUpdated: Date.now() };
        }
        return order;
      }).filter(order => order.items.length > 0);
    });
  };

  const updateItemExtras = (itemId: string | number, restaurantId: string, extras: Extra[]) => {
    setCartOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.restaurantId === restaurantId) {
          const updatedItems = order.items.map(item => {
            const currentId = item.type === 'pack' ? item.pack?.id : item.dish?.id;
            return currentId === itemId ? { ...item, selectedExtras: extras } : item;
          });
          return { ...order, items: updatedItems, lastUpdated: Date.now() };
        }
        return order;
      });
    });
  };

  const clearOrder = (restaurantId?: string) => {
    if (restaurantId) {
      setCartOrders(prev => prev.filter(o => o.restaurantId !== restaurantId));
    } else {
      setCartOrders([]);
    }
  };

  const totalItems = cartOrders.reduce((total, order) =>
    total + order.items.reduce((sum, item) => sum + item.quantity, 0), 0
  );

  const totalPrice = cartOrders.reduce((total, order) =>
    total + order.items.reduce((sum, item) => {
      const extrasCost = (item.selectedExtras || []).reduce((eSum, extra) => eSum + extra.price, 0);
      const unitPrice = item.type === 'pack' ? (item.pack?.packPrice || 0) : (item.dish?.price || 0);
      return sum + (unitPrice + extrasCost) * item.quantity;
    }, 0), 0
  );

  return (
    <OrderContext.Provider value={{
      cartOrders,
      addToOrder,
      updateQuantity,
      updateItemExtras,
      totalPrice,
      totalItems,
      clearOrder
    }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};